import * as THREE from 'three';
import { G, K_BEV } from '../../render/scene';
import { rr, rri } from '../../core/rng';
import { CLIMB_MAX, STEP, VAULT } from '../terrain';
import { glowTex } from '../../render/ground';
import { MATKEYS, flushBuckets, put, resetBuckets } from './ruin-kit';
import { buildContactShadows, resetFootprints } from '../../render/contact-shadow';
import { BEAM_N, convoy, firePins, resetBeams, resetPins, searchlight, signboard, wreck } from './landmarks';
export { animBeams } from './landmarks';

/* ============================================================
   空曠場地 —— 唯一的戰鬥場
   ============================================================

   使用者 2026-08-04 分兩次拍板：

     ① 丟掉整張移植進來的城，改成**空曠、看不到邊的場地**
     ② 畫風照廢棄都市那一套 —— 混凝土、鏽蝕、危險條紋、黃昏低角度光
     ③ 加動態光源（還在燒的殘骸）
     ④ **拿掉高低起伏**（原話：「把起伏拿掉好了」）—— 地面是平的

   所以這個檔是「開放版面 × 廢墟材質」：**平的地面**上散著走不過的量體，
   單一量體看起來像一棟被炸掉一半的樓。材質語言在 `ruin-kit.ts`。

   ⚠️ 場上**沒有任何 `stand: true` 的構件** —— 那是「地面是平的」的
   機器可驗版本（`field.test.ts` 有一條在守它）。哪天又要做高低差，
   `world/terrain.ts` 的高度場與 `STEP`/`VAULT` 都還在，加回來就好。

   ## 尺寸全部是從「我們自己的機體與敵人」反推的

   使用者：「也要調整跟我機甲和敵人適合的場地」。兩個既有數字定了一切：

     飛行上限 **4.84 m** ──→ 純牆最矮 **10 m**（`CLIMB_MAX` 8 m 的上面一階），
                              而且 `stand:false` ── 飛到頂也站不住
     同屏 **300 隻** ──────→ 量體之間至少留 12 m（量體外接正方形之間），
                              讓 r=20 m 的風箏環在絕大多數位置畫得出來

   ## 兩個「只有這樣做才成立」的地方

   1. **投放用抖動網格，不是隨機撒點。** 隨機撒點在間距限制下只塞得進三分之一，
      而且會留下大片空洞（實測走了 150 m 一座都沒看到）。網格保證「沒有空洞」，
      間距檢查保證「沒有夾縫」，兩件事各由一半負責。
   2. **`rot` 與 three 的 `rotation.y` 反向。** 構件的 `rot` 進
      `pointBlocked`／`resolveCircle` 時算的是 `R_y(rot)`，而 mesh 用的是
      `R_y(-rot)` —— 所以 `solid()` 一律 `rot: -ry`，只在這一個地方轉換。
      漏掉的話薄長的量體會整片橫躺在錯的方向，**而畫面上完全看不出來**。
   ============================================================ */

/** 純牆的最低高度。飛行上限 4.84 m、可站上限 `CLIMB_MAX` 8 m ——
    比兩者都高，才讀得出「這個上不去」。 */
export var WALL_MIN_H = 10;
/** 量體之間的最小淨空。**這是密度的唯一旋鈕**，而它有上下兩個邊界：
    調小 → 量體開始貼在一起，`60 m 視窗覆蓋峰值` 會破 22%；
    調大 → 網格上大部分格子被鄰居擋掉，密度掉一半（20 m 只放得下 34 座）。
    現值 16 m 搭配 40 m 的網格：相鄰兩格的最小中心距 28 m ＝ 兩邊半寬 ＋ 16。
    **場地邊界也算一個量體**：貼著邊放會留一條 6–10 m 的夾縫。 */
export var MIN_GAP = 17;
/** 開場站位周圍的淨空半徑：一開局不要被牆貼臉。 */
export var SPAWN_CLEAR = 20;

var _obs: any = null;
var placed: any[] = [];

/** 登記一個方形構件。**`rot` 在這裡取負號**（見檔頭第 3 點）。 */
function solid(x, z, ry, w, d, h, stand) {
  return _obs({ kind: 0, x: x, z: z, rot: -(ry || 0), hw: w / 2, hd: d / 2, h: h, stand: stand, fixed: true });
}
/** 這個矩形（半邊長 `hw`×`hd`）放得下嗎：離出生點與其他量體都要有 `gap`。

    ⚠️ **判定用矩形，不准用外接圓。** 一座 132 m 見方的台地外接圓半徑是 93 m，
    六座就把 480 m 見方的場地佔滿 —— 於是量體只放得進 32 座，
    而症狀是「走了 150 m 才看到第一個東西」，離真因（一行判定式）非常遠。
    `lessons.md` 2026-08-01 記過同一條：物件越大，外接球越失真。 */
function gapOK(x, z, hw, hd, gap) {
  var dx = Math.max(0, Math.abs(x) - hw), dz = Math.max(0, Math.abs(z) - hd);
  if (Math.hypot(dx, dz) < SPAWN_CLEAR) return false;
  for (var i = 0; i < placed.length; i++) {
    var p = placed[i];
    if (Math.abs(x - p.x) < hw + p.hw + gap && Math.abs(z - p.z) < hd + p.hd + gap) return false;
  }
  return true;
}
function facKey() { return rr(0, 1) < .4 ? 'facA' : (rr(0, 1) < .5 ? 'facB' : 'facC'); }

/* ---------- 造型 ---------- */

/** 散落的碎塊。**不進 `obstacles`**（0.5 m 高，碰撞上等於沒有）——
    純粹讓量體的腳下不是一條乾淨的線，那是「廢墟」與「積木」的差別。 */
function rubble(x, z, n, spread) {
  for (var i = 0; i < n; i++) {
    var a = rr(0, 6.283), r = spread * rr(0.5, 1.15);
    put('conD', G.box, 'box', rr(1.4, 3.8), rr(.4, 1.0), rr(1.4, 3.8),
      x + Math.cos(a) * r, rr(.2, .5), z + Math.sin(a) * r,
      rr(-.3, .3), rr(0, 6.283), rr(-.3, .3), 4);
  }
}

/**
 * 崩塌的高樓（22–40 m）：走不過、飛不上去的第四階「牆」。
 * 分段收縮 + 樓板橫帶 + 削掉的頂端 + 外露鋼筋 —— 這四樣是
 * 「一棟被炸掉一半的樓」與「一根長方體」的全部差別。
 */
function tower(x, z, ry, w, d, h) {
  var fk = facKey(), segs = Math.max(2, Math.round(h / 13)), y = 0, cw = w, cd = d;
  for (var s = 0; s < segs; s++) {
    var sh = h / segs * rr(.82, 1.18);
    if (s === segs - 1) sh = Math.max(2, h - y) * rr(.5, 1.0);      // 頂端被削掉
    put(fk, G.bev, 'cyl', cw * K_BEV, sh, cd * K_BEV, x, y + sh / 2, z, 0, ry, 0, 17);
    put('conD', G.box, 'box', cw * 1.06, .42, cd * 1.06, x, y + sh, z, 0, ry, 0, 5);
    y += sh; cw *= rr(.90, .98); cd *= rr(.90, .98);
  }
  /* 崩塌的斷面：外露樓板 + 鋼筋 */
  for (var t = 0; t < 4; t++)
    put('conD', G.box, 'box', cw * rr(.45, 1.05), .42, cd * rr(.45, 1.05),
      x + rr(-cw * .3, cw * .3), y + rr(-4, 2), z + rr(-cd * .3, cd * .3),
      rr(-.42, .42), rr(0, 6.283), rr(-.42, .42), 5);
  for (var u = 0; u < 6; u++)
    put('rust', G.cyl, 'cyl', .13, rr(1.6, 4.2), .13,
      x + rr(-cw * .5, cw * .5), y + rr(-.5, 2.4), z + rr(-cd * .5, cd * .5),
      rr(-.6, .6), 0, rr(-.6, .6), 2);
  /* 側面外掛的管線 */
  for (var v = 0; v < Math.round(h / 10); v++) {
    var side = rr(0, 1) < .5 ? 1 : -1, yy = rr(4, h - 3);
    put('steel', G.box, 'box', w * .16, .5, d * 1.16,
      x + Math.cos(ry) * side * (w * .52), yy, z - Math.sin(ry) * side * (w * .52), 0, ry, 0, 3);
  }
  /* 頂端警示燈 —— 冷白，且只在夠高的塔上（不是威脅色，不會被誤讀成敵人） */
  if (h > 26) put('lit', G.box, 'box', 1.2, .5, 1.2, x, y + 1.0, z);
  rubble(x, z, 6, Math.max(w, d) * .62);
  solid(x, z, ry, w, d, h, false);
}

/**
 * 混凝土碉堡（10–16 m）：一樣走不過、飛不上去，但矮到看得見後面。
 * 頂緣一條危險條紋 —— 那是「這裡上不去」的標示。
 */
function bunker(x, z, ry, w, d, h) {
  put('con', G.bev, 'cyl', w * K_BEV, h * .9, d * K_BEV, x, h * .45, z, 0, ry, 0, 11);
  /* ⚠️ 危險條紋是**頂緣的一圈帶子**，不是蓋住整個頂面的一塊板。
     `haz` 的斜紋對比很高，鋪滿 17 m 見方的頂面就是一塊斑馬布，
     從俯視鏡頭看整個畫面都是它 —— 使用者 2026-08-04 回報的第二次「反白」。
     做法：帶子比本體寬（w×1.1）、頂蓋比本體窄（w×1.02），
     於是從上面看只露出一圈約 0.7 m 的條紋邊。 */
  put('haz', G.box, 'box', w * 1.1, 1.0, d * 1.1, x, h * .9 - .5, z, 0, ry, 0, 1.6);
  put('conD', G.box, 'box', w * 1.02, h * .12, d * 1.02, x, h * .93, z, 0, ry, 0, 5);
  put('steel', G.box, 'box', .34, h * .92, .34, x + Math.cos(ry) * (w / 2 - .3), h * .46, z - Math.sin(ry) * (w / 2 - .3), 0, ry, 0, 3);
  put('steel', G.box, 'box', .34, h * .92, .34, x - Math.cos(ry) * (w / 2 - .3), h * .46, z + Math.sin(ry) * (w / 2 - .3), 0, ry, 0, 3);
  rubble(x, z, 5, Math.max(w, d) * .6);
  solid(x, z, ry, w, d, h, false);
}

/**
 * 倒塌的大樓：2–4 節橫躺的樓體，一節接一節、每節歪一點。
 * **開放場地最有辨識度的一種量體** —— 它是「橫」的，
 * 在一片直立的方塊裡一眼就分得出來，而且它自然形成一條長掩體。
 */
function toppled(x, z, ry) {
  /* ⚠️ **先算完長度再放**。一列倒塌大樓可以延伸到 110 m，
     而它在版面上只佔一格 —— 第一版只替它保留 46 × 46 的位置，
     於是後面的網格量體直接疊在樓身上，把場地切出走不到的口袋
     （`pnpm layout` 的連通塊從 1 變成 9）。 */
  /* ⚠️ 節與節之間的縫要嘛**幾乎貼著**（<1.5 m，讀成一道裂縫），
     要嘛就得寬到走得過去（>12 m）。中間那一段是最糟的：
     4–6 m 的縫會圍出一格 4 m² 的死角，`layout-metrics` 把它算成
     一塊獨立的連通塊（實測連通塊 1 → 13，而畫面上完全看不出來）。 */
  /* 長度也有上限：一列 50 m × 14 m 的樓體在 60 × 60 m 的取樣窗裡就佔掉
     22%，剛好等於「視窗覆蓋峰值」的規格上限 —— 再加一個鄰居就破表。 */
  /* 尺寸（2026-08-04 縮過一次）：3 節 × 17 m × 12 m 是四幕裡**唯一**
     會把 60 m 視窗覆蓋峰值頂破 22% 的東西 —— 量測時碰撞還要外擴玩家
     半徑 1.3，所以 17×12 的一節實際佔 19.6×14.6，三節就是 23.8%。
     縮到 11–15 × 8–10 之後峰值有 5 個百分點的餘裕給敘事地標。 */
  var n = rri(2, 3), lens: number[] = [], gaps: number[] = [], total = 0;
  for (var i = 0; i < n; i++) {
    var len = rr(11, 15); lens.push(len);
    var gp = rr(.6, 1.4); gaps.push(gp);
    total += len + (i < n - 1 ? gp : 0);
  }
  var w = rr(8, 10), hh = rr(11, 16);
  var half = total / 2 + w * 0.6;
  if (!gapOK(x, z, half, half, MIN_GAP)) return 0;

  var fk = facKey(), c = Math.cos(ry), s2 = Math.sin(ry), px = -total / 2;
  for (var k = 0; k < n; k++) {
    /* 橫向抖動壓到 ±0.8：±1.6 加上整列的旋轉會在兩節之間張出一個
       6–10 m 的楔形縫 —— 而那寬度剛好卡在「走得過去」與「圍出死角」之間，
       `pnpm layout` 的「6–10 m 縫隙」就是在守它。 */
    var L = lens[k], lx = px + L / 2, lz = rr(-.8, .8);
    var wx = x + lx * c + lz * s2, wz = z - lx * s2 + lz * c;
    put(fk, G.box, 'box', L, hh, w, wx, hh * rr(.44, .54), wz, rr(-.12, .12), ry, rr(-.2, .2), 17);
    /* 斷面的樓板 */
    put('conD', G.box, 'box', 1.2, hh * .94, w * .96,
      wx + (L / 2) * c, hh * .5, wz - (L / 2) * s2, 0, ry, 0, 5);
    solid(wx, wz, ry, L, w, hh, false);
    px += L + gaps[k];
  }
  rubble(x, z, 16, total * .4);
  placed.push({ x: x, z: z, hw: half, hd: half });
  return total;
}

/** 貨櫃堆：三層、鋼板與鏽蝕交替。整堆 10.2 m —— 剛好過純牆的門檻。

    ⚠️ **不要用 `haz` 當貨櫃外皮。** 危險條紋的對比很高、圖案很大，
    貼在 12 m 的面上會變成一塊巨大的斑馬板（tile 3 ＝ 一條斜紋 3 m 寬）。
    `haz` 只留給「一圈警示帶」那種細長的用法。
    鏽蝕也要壓 —— 飽和的橘是威脅色 amber 的地盤，地形不准跟它搶讀。 */
function containers(x, z, ry) {
  var CH = 3.4, n = 3;
  for (var s = 0; s < n; s++) {
    var tilt = s === n - 1 && rr(0, 1) < .25;
    var kx = x + rr(-1.2, 1.2), kz = z + rr(-1.2, 1.2);
    var kr = ry + (rr(0, 1) < .5 ? 0 : Math.PI / 2) + rr(-.12, .12);
    var y = CH / 2 + s * CH + (tilt ? 1.1 : 0), rz = tilt ? rr(.15, .4) : 0;
    put(rr(0, 1) < .45 ? 'rust' : 'steel', G.box, 'box', 12, CH, 5.4, kx, y, kz, 0, kr, rz, 2.2);
    /* 側面的加強肋：貨櫃跟「一塊長方體」的差別就在這幾條 */
    for (var r2 = 0; r2 < 3; r2++)
      put('trim', G.box, 'box', .3, CH * .92, 5.6,
        kx + Math.cos(kr) * (r2 - 1) * 3.6, y, kz - Math.sin(kr) * (r2 - 1) * 3.6, 0, kr, rz);
  }
  rubble(x, z, 5, 9);
  solid(x, z, ry, 13, 13, CH * n, false);
}

/**
 * 天線塔：四支腳 + 每 8 m 一層橫撐 + 頂端一盞警示燈。
 * 34–56 m 高，**天際線的節奏**靠它 —— 一片同高的方塊會讀成一張桌子。
 */
function antenna(x, z, ry) {
  var h = rr(34, 56), leg = 1.4;
  for (var k = 0; k < 4; k++) {
    var ox = (k & 1 ? 1 : -1) * 2.6, oz = (k & 2 ? 1 : -1) * 2.6;
    put('steel', G.box, 'box', .7, h, .7,
      x + ox * Math.cos(ry) + oz * Math.sin(ry), h / 2, z - ox * Math.sin(ry) + oz * Math.cos(ry), 0, ry, 0, 4);
  }
  for (var y = 5; y < h; y += 8) {
    put('steel', G.box, 'box', 6.4, .32, 6.4, x, y, z, 0, ry, 0, 4);
    put('rust', G.box, 'box', 8.2, .26, .26, x, y + 2.8, z, 0, ry, .7, 3);
  }
  put('lit', G.box, 'box', 1.1, .5, 1.1, x, h + 1.2, z);
  solid(x, z, ry, 7 + leg, 7 + leg, h, false);
}

/**
 * 門式起重機：一根長臂 + 兩支腳（有一半是倒的）。
 * 佔地很寬（~46 m），所以跟倒塌大樓一樣走「特殊件」的投放路徑。
 */
function crane(x, z, ry, fallen) {
  /* 長臂 64 m ＋ 兩支腳跨 42 m —— 跟倒塌大樓同一個坑，位置要先保留夠大 */
  if (!gapOK(x, z, 36, 36, MIN_GAP)) return false;
  var H = fallen ? rr(20, 26) : rr(32, 40);
  put('rust', G.box, 'box', 64, 3, 3.6, x + 8 * Math.cos(ry), H * (fallen ? .72 : 1) , z - 8 * Math.sin(ry),
    0, ry, fallen ? -.34 : -.04, 8);
  for (var k = 0; k < 2; k++) {
    var sx = k ? 21 : -21;
    put('rust', G.box, 'box', 3, H, 3,
      x + sx * Math.cos(ry), H / 2, z - sx * Math.sin(ry), 0, ry, fallen ? (sx > 0 ? .3 : -.16) : (sx > 0 ? .05 : -.05), 6);
    solid(x + sx * Math.cos(ry), z - sx * Math.sin(ry), ry, 6, 6, H, false);
  }
  rubble(x, z, 16, 26);
  placed.push({ x: x, z: z, hw: 36, hd: 36 });
  return true;
}

/** 殘牆：一截還立著的混凝土牆。矮（5–9 m）但一樣走不過、站不上去 ——
    它的工作是**擋視線不擋天空**，讓場上不是只有「高塔」與「空地」兩種狀態。 */
function ruinWall(x, z, ry, w, d) {
  var h = rr(5, 9);
  put('con', G.box, 'box', w, h, d, x, h / 2, z, 0, ry, rr(-.05, .05), 12);
  put('conD', G.box, 'box', w * 1.02, .4, d * 1.6, x, h, z, 0, ry, 0, 5);
  /* 露出來的鋼筋 */
  for (var i = 0; i < 4; i++)
    put('rust', G.cyl, 'cyl', .12, rr(1.2, 2.8), .12,
      x + Math.cos(ry) * rr(-w * .45, w * .45), h + rr(.4, 1.4), z - Math.sin(ry) * rr(-w * .45, w * .45),
      rr(-.5, .5), 0, rr(-.5, .5), 2);
  rubble(x, z, 6, w * .5);
  solid(x, z, ry, w, d, h, false);
}

/* ---------- 版面 ---------- */

/**
 * 蓋一張場地。**吃 `RNG`**（呼叫端已經照幕序號下過種子），
 * 所以同一顆種子每一幕的場地都一樣。
 */
export function buildField(group: THREE.Group, addObstacle: (o: any) => any, R: number) {
  _obs = addObstacle;
  resetBuckets(); resetFootprints();
  resetBeams(); resetPins();
  placed.length = 0;

  /* ⓪ **敘事地標**（`landmarks.ts`）。放在最前面，因為它們佔地最寬、
     而且它們是玩家一定要看到的東西 —— 讓後面的網格量體去閃它們，
     不是反過來讓地標去找空位。

     ⚠️ 數量刻意很少（一件墜落構造體、兩座探照燈塔、一到兩隊車隊）。
     使用者 2026-08-04：「場景也不能設計過於擁擠，畢竟是類倖存者遊戲，
     空曠的場地很重要」—— 故事性用**稀疏但資訊量高的東西**換，
     不用密度換。車隊與路牌**不進 `obstacles`**，走得過去。 */
  for (var w1 = 0, gw = 0; w1 < 1 && gw < 60; gw++) {
    var kx = rr(-R + 90, R - 90), kz = rr(-R + 90, R - 90);
    if (wreck(kx, kz, rr(0, 6.283), gapOK, function (p) { placed.push(p); }, solid, MIN_GAP)) w1++;
  }
  for (var s1 = 0, gs = 0; s1 < BEAM_N && gs < 120; gs++) {
    var sx = rr(-R + 60, R - 60), sz = rr(-R + 60, R - 60);
    if (!gapOK(sx, sz, 14, 14, MIN_GAP)) continue;
    searchlight(group, sx, sz, solid);
    placed.push({ x: sx, z: sz, hw: 14, hd: 14 });
    s1++;
  }
  for (var v1 = 0, gv = 0; v1 < 2 && gv < 120; gv++) {
    var vx = rr(-R + 70, R - 70), vz = rr(-R + 70, R - 70), vr = rr(0, 6.283);
    /* 車隊是裝飾，但它**還是要一塊淨空** —— 疊在一棟樓裡的車隊
       讀不出是車隊，只讀成建築底下的雜訊。 */
    if (!gapOK(vx, vz, 34, 34, 4)) continue;
    convoy(vx, vz, vr);
    if (v1 === 0) signboard(vx + Math.sin(vr) * 26, vz + Math.cos(vr) * 26, vr);
    placed.push({ x: vx, z: vz, hw: 30, hd: 30 });
    v1++;
  }

  /* ① 特殊件：佔地很寬的倒塌大樓與門式起重機。先放，讓網格去閃它們。
     造型詞彙取自使用者的 `world_sample.html`（`buildToppled`／`buildYard`
     的起重機）—— 使用者原話：「地圖中有些造景或障礙物佈置可以參考
     sample 裡面的建築」。 */
  var big = rri(2, 4);
  for (var b1 = 0, gb = 0; b1 < big && gb < 300; gb++) {
    var bx = rr(-R + 80, R - 80), bz = rr(-R + 80, R - 80), br = rr(0, 6.283);
    /* 兩支建造器**自己**做間距檢查並登記位置（它們比呼叫端更清楚自己多大） */
    var ok = rr(0, 1) < 0.62 ? toppled(bx, bz, br) > 0 : crane(bx, bz, br, rr(0, 1) < 0.5);
    if (ok) b1++;
  }

  /* ② 走不過也飛不上去的量體。

     **投放方式是抖動網格，不是隨機撒點。** 隨機撒點在 20 m 的間距限制下
     只塞得進 48 座（要 126 座，四千次嘗試全部用完），而且分佈很不均 ——
     實看時走了 30 m 一座都沒看到，畫面上完全是一片空地。
     網格保證「沒有大片空洞」，間距檢查保證「沒有夾縫」，兩件事各由一半負責。

     密度的判準要對照**鏡頭視野**而不只是整場統計：跟隨鏡頭一次看到約
     60 × 35 m ＝ 2,100 m²，所以「平均每 1,000～1,500 m² 一座」才會
     每個畫面都看得到東西。 */
  var CELL = 30, half = Math.floor(R / CELL);
  var cells: any[] = [];
  for (var ci = -half; ci <= half; ci++) for (var cj = -half; cj <= half; cj++) cells.push([ci, cj]);
  /* 洗牌：照順序放的話西南角永遠先搶到位置，東北角永遠被間距檢查擋掉 */
  for (var sh = cells.length - 1; sh > 0; sh--) {
    var sj = rri(0, sh), tmp = cells[sh]; cells[sh] = cells[sj]; cells[sj] = tmp;
  }
  var nw = 0;
  for (var c2 = 0; c2 < cells.length; c2++) {
    /* 留一部分格子當空地。**0.12 → 0.19**（2026-08-04）：使用者補了
       「場景也不能設計過於擁擠，畢竟是類倖存者遊戲，空曠的場地很重要」，
       而這一輪又加了三種敘事地標 —— 量體讓出來的空間就是給它們的。 */
    if (rr(0, 1) < 0.19) continue;

    /* ⚠️ **先決定種類與尺寸，再做間距檢查。** 第一版是「先抽一組 6–12 m 的
       尺寸做檢查，再挑造型」—— 而殘牆長 26 m、貨櫃堆 12 × 12，實際佔地
       跟檢查用的尺寸對不上，於是量體互相疊進去，把場地切出走不到的口袋
       （連通塊 1 → 13）。這是這一輪第三次踩「保留的位置 ≠ 真正的佔地」。 */
    var kind = 0, ww = 0, wd = 0, pk = rr(0, 1);
    if (pk < 0.16) { kind = 0; ww = rr(8, 15); wd = rr(8, 15); }          // 崩塌高樓
    else if (pk < 0.24) { kind = 1; ww = 9; wd = 9; }                     // 天線塔
    else if (pk < 0.46) { kind = 2; ww = 13; wd = 13; }                   // 貨櫃堆
    else if (pk < 0.70) { kind = 3; ww = rr(13, 21); wd = rr(2.6, 4); }   // 殘牆
    else { kind = 4; ww = rr(7, 14); wd = rr(7, 14); }                    // 碉堡

    var wx = cells[c2][0] * CELL + rr(-6, 6), wz = cells[c2][1] * CELL + rr(-6, 6);
    /* 離場地邊界也要留 22 m：**邊界本身就是一道牆**，貼著放會在量體與
       邊界之間留一條 6–10 m 的夾縫。 */
    var lim = R - 22;
    if (Math.abs(wx) > lim - ww / 2 || Math.abs(wz) > lim - wd / 2) continue;
    /* 間距用**外接正方形**：量體等一下要隨機轉一個角度，轉 45° 時
       實際佔用的軸向寬度是 hypot(w,d)。 */
    var wr = Math.hypot(ww, wd) / 2;
    if (!gapOK(wx, wz, wr, wr, MIN_GAP)) continue;

    var ry = rr(0, 6.283);
    /* 種類分佈刻意有極端值：多數是矮的（看得見後面），少數拉到 22–56 m
       —— 天際線上要有節奏，不然空曠場地會讀成一張擺了積木的桌子。
       五種造型的詞彙取自使用者的 `world_sample.html`（原話：「地圖中有些
       造景或障礙物佈置可以參考 sample 裡面的建築」）。 */
    if (kind === 0) tower(wx, wz, ry, ww, wd, rr(22, 40));
    else if (kind === 1) antenna(wx, wz, ry);
    else if (kind === 2) containers(wx, wz, ry);
    else if (kind === 3) ruinWall(wx, wz, ry, ww, wd);
    else bunker(wx, wz, ry, ww, wd, rr(WALL_MIN_H, 16));
    placed.push({ x: wx, z: wz, hw: wr, hd: wr });
    nw++;
  }

  var made = flushBuckets(group);
  /* 接地影：**一定要在 `flushBuckets()` 之後**（那時候所有零件都已經
     `put()` 過、足跡才收齊）。收在這裡而不是各建造器裡的理由見
     `render/contact-shadow.ts` 檔頭。 */
  var shadows = buildContactShadows(group);
  /* 還在燒的殘骸（動態光源）—— 掛在已放置的量體腳下 */
  buildFires(group, 14);
  return { meshes: made, walls: nw, fires: fires.length, shadows: shadows };
}

/* ============================================================
   動態光源：還在燒的殘骸
   ============================================================

   使用者 2026-08-04：「另外加動態光源」。空曠場地的問題是**沒有東西在動**
   —— 雲影太慢、塵太小，一整片靜止的暗地面看久了就是一張圖。
   燃燒的殘骸同時解掉三件事：畫面有動、地面有暖色錨點、
   而且它是「這裡發生過什麼」的敘事（廢棄都市的畫風要的就是這個）。

   ## 三個成本上的決定

   1. **點光源只有 `FIRE_N` 盞**（現值 4）。`MeshStandardMaterial` 的
      點光源是**逐片元**計算的，而地面鋪滿整個畫面 —— 加一盞就是整個
      螢幕多一輪運算，跟它照得到多遠無關。原型也只用 3 盞。
   2. **火焰與煙用 Sprite，不用點光源。** 亮度的來源是加算混合，
      不是光照 —— 視覺上分不出來，成本差一個數量級。
   3. **煙柱回收**：五顆精靈往上飄，越過頂就回到底部，不重新配置物件。

   ⚠️ 這一整段跑在**畫面幀**上（`animField` → `animFires`），純視覺、
   不進模擬、不影響決定性。 */

/** 幾盞真的點光源。**這是效能旋鈕**，見上面第 1 點。
    火源本身有 14 處（精靈很便宜），但只有最前面這幾處配點光源。 */
export var FIRE_N = 4;
/** 一處火源掛幾顆煙。 */
var SMOKE_N = 5;

var fires: any[] = [];
var fireGroup: THREE.Group | null = null;

function sprite(tex, x, y, z, size, color, op) {
  var s = new THREE.Sprite(new THREE.SpriteMaterial({
    map: tex, color: color, blending: THREE.AdditiveBlending,
    depthWrite: false, transparent: true, opacity: op, fog: true,
  }));
  s.position.set(x, y, z); s.scale.set(size, size, 1);
  return s;
}

/** 在 `n` 個量體腳下點火。**挑量體而不是隨機撒點**：火要有燒的東西，
    憑空燒起來的一團橘光讀成特效，燒在一棟塌了一半的樓下面才讀成廢墟。 */
function buildFires(group: THREE.Group, n: number) {
  fires.length = 0;
  fireGroup = new THREE.Group();
  group.add(fireGroup);
  var tx = glowTex();
  /* **先燒 `landmarks.ts` 登記的火點**（燒毀的頭車、墜落構造體的斷面）：
     那些地方本來就該在燒，火與物件的因果讀得出來。用完了再回到量體腳下。 */
  var pins = firePins().slice();
  var pool = placed.slice();
  for (var i = 0; i < n && (pool.length || pins.length); i++) {
    var fx, fz;
    if (pins.length) {
      var qi = rri(0, pins.length - 1), q = pins[qi]; pins.splice(qi, 1);
      fx = q.x + rr(-2, 2); fz = q.z + rr(-2, 2);
    } else {
      var pi = rri(0, pool.length - 1), p = pool[pi];
      pool.splice(pi, 1);
      /* 貼在量體的側邊（不是正中央，那裡被量體本身擋住） */
      var a = rr(0, 6.283);
      fx = p.x + Math.cos(a) * (p.hw + rr(3, 7));
      fz = p.z + Math.sin(a) * (p.hd + rr(3, 7));
    }

    var core = sprite(tx, fx, 1.4, fz, rr(7, 11), 0xffb066, .5);
    var halo = sprite(tx, fx, 2.2, fz, rr(18, 26), 0xff5a18, .22);
    fireGroup.add(core); fireGroup.add(halo);

    /* 前 `FIRE_N` 處給真的點光源，其餘只有精靈 —— 畫面上分不出來 */
    var light: any = null;
    if (i < FIRE_N) {
      light = new THREE.PointLight(0xff7326, 0, 58, 2);
      light.position.set(fx, 3.2, fz);
      fireGroup.add(light);
    }
    var smoke: any[] = [];
    for (var k = 0; k < SMOKE_N; k++) {
      var sp = sprite(tx, fx, 4 + k * 7, fz, 12 + k * 5, 0x4a4f58, .12);
      fireGroup.add(sp); smoke.push(sp);
    }
    fires.push({ x: fx, z: fz, core: core, halo: halo, light: light, smoke: smoke, t: rr(0, 6.283) });
  }
}

/** 每幀：火焰閃爍 ＋ 煙柱上升。**跑在畫面幀上**（純視覺）。 */
export function animFires(dt: number) {
  for (var i = 0; i < fires.length; i++) {
    var f = fires[i];
    f.t += dt * (1.7 + i * 0.13);
    /* 兩個不同週期的正弦疊加 —— 單一正弦讀成「呼吸」，
       兩個不同週期才讀成「火在燒」（不規則但不隨機）。 */
    var k = 0.62 + 0.38 * Math.sin(f.t) * Math.sin(f.t * 2.7 + 1.1);
    f.core.material.opacity = 0.34 + 0.30 * k;
    f.core.scale.setScalar(8 + 3 * k);
    f.halo.material.opacity = 0.14 + 0.14 * k;
    if (f.light) f.light.intensity = 1.5 + 1.5 * k;
    for (var s = 0; s < f.smoke.length; s++) {
      var sp = f.smoke[s];
      sp.position.y += dt * 3.4;
      sp.position.x += dt * 1.9;                    // 跟塵同一個風向
      var life = (sp.position.y - 4) / 40;
      sp.material.opacity = 0.13 * Math.max(0, 1 - life);
      sp.scale.setScalar(12 + life * 26);
      if (sp.position.y > 44) { sp.position.set(f.x, 4, f.z); }
    }
  }
}

/** 給測試與診斷讀的規格（不要在別處重算一份）。 */
export var FIELD_SPEC = {
  WALL_MIN_H: WALL_MIN_H, MIN_GAP: MIN_GAP, SPAWN_CLEAR: SPAWN_CLEAR,
  STEP: STEP, VAULT: VAULT, CLIMB_MAX: CLIMB_MAX, MATKEYS: MATKEYS,
};
